import { CTInput, CTButton, CTDatePicker, CTCard, CTLabel, CTProgressCircular } from "@nbcuniversal/ct-ui";
import React, { useEffect, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Link } from "react-router-dom";

import { API_PATH, REDIRECTS } from "../../../Configurations";
import usePageTitle from "../../../hooks/UsePageTitle";
import checkMaintenanceMode from "../../../MaintenanceModeUtil";
import "./RegistrationForm.scss";

interface RegistrationPost {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  last4OfSocial: string;
  personalEmail: string;
  sso?: string;
}

class RecaptchaError extends Error {
  public constructor(message: string, public originalError?: unknown) {
    super(message);
    this.name = "RecaptchaError";
  }
}

const RegistrationForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  usePageTitle("Alumni Registration");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [last4OfSocial, setLast4OfSocial] = useState("");
  const [personalEmail, setPersonalEmail] = useState("");
  const [nbcSso, setNbcSso] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [formEmailReenter, setFormEmailReenter] = useState("");
  const [shouldDisplaySSN, setShouldDisplaySSN] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [requestErrorMsg, setRequestErrorMsg] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [invalidEmailMatch, setInvalidEmailMatch] = useState(false);
  const [validEmailFormat, setValidEmailFormat] = useState<boolean | null>(null);
  const [warnEmail, setWarnEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    checkEmailMatch();
  }, [personalEmail, formEmailReenter]);

  const handleRecaptchaVerification = async (): Promise<string> => {
    if (!executeRecaptcha) {
      console.error("reCAPTCHA not initialized. executeRecaptcha method not available.");
      throw new RecaptchaError("reCAPTCHA service unavailable");
    }

    try {
      const token = await executeRecaptcha("submit");

      if (!token) {
        throw new RecaptchaError("Failed to generate reCAPTCHA token");
      }

      return token;
    }
    catch (error) {
      console.error("reCAPTCHA verification failed:", error);
      if (error instanceof RecaptchaError) {
        throw error;
      }
      throw new RecaptchaError("reCAPTCHA verification failed. Please try again.", error);
    }
  };

  const customEmailValidator = (email: string): boolean | null => {
    if (!email) {
      setWarnEmail(false);
      return true; // No error if value is empty
    }
    const companyDomains = ["nbcuni.com", "universalorlando.com", "udx.com", "cnbc.com", "msnbc.com", "telemundo.com", "comcast.net", "uniparks.com"];
    // eslint-disable-next-line max-len, no-control-regex
    const emailRegex = /^(?!.*\s)(?!.*,,)(?!.*\.\.)(?!.*@.*@)[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\t\x0b\x0c\x0e-\x7f])+)\])$/i;
    if (emailRegex.test(email)) {
      const domain = email.split("@")[1];
      if (companyDomains.includes(domain.toLowerCase())) {
        setWarnEmail(true);
      }
      else {
        setWarnEmail(false);
      }
      return true;
    }
    else {
      return false;
    }
  };

  const checkEmailMatch = () => {
    if (!formEmailReenter) return;
    setInvalidEmailMatch(personalEmail !== formEmailReenter);
  };

  const onSubmit = async () => {
    if (invalidEmailMatch) {
      return;
    }

    setIsLoading(true);
    setMaskedEmail("");
    let currentRequestError = false;
    try {
      const token = await handleRecaptchaVerification();

      const data: RegistrationPost = {
        firstName,
        lastName,
        dateOfBirth,
        last4OfSocial,
        personalEmail,
      };
      // optional and only provide if the value is set
      if (nbcSso) {
        data.sso = nbcSso;
      }
      // Check we are not in maintenance prior to posts..
      if (await checkMaintenanceMode() === true) {
        setIsLoading(false);
        currentRequestError = true;
        setRequestError(currentRequestError);
        const msg = "Portal is in Maintenance Mode";
        setRequestErrorMsg(msg);
        throw new Error(msg);
      }

      const response = await fetch(`${API_PATH}/registration`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-recaptcha-token": token,
        },
        body: JSON.stringify(data),
      });

      if (response.status === 403) {
        const deniedRequestMessage = "You have been detected as a robot. Please use a different browser or try again later.";
        currentRequestError = true;
        setRequestError(currentRequestError);
        setRequestErrorMsg(deniedRequestMessage);
        console.error(deniedRequestMessage);
        setIsLoading(false);
        return;
      }

      const result = await response.json();
      // Need to explicitly check for true/false as isNewUser could be undefined in error cases
      if (result.isNewUser === true) {
        window.location.href = REDIRECTS.newUser as string;
      }
      else if (result.isNewUser === false) {
        window.location.href = REDIRECTS.login as string;
      }
      else {
        setRequestError(true);
        if (result.message !== "" && result.message !== undefined) {
          setRequestErrorMsg(result.message);
        }
        else {
          setRequestErrorMsg("There was an Error processing your request.");
        }
        setMaskedEmail(result.maskedEmail ?? "");
        setIsLoading(false);
        return;
      }
    }
    catch (error: any) {
      // Check if this is a reCAPTCHA specific error
      if (error instanceof RecaptchaError) {
        setRequestError(true);
        setRequestErrorMsg("Security verification failed. Please refresh the page and try again.");
        console.error("reCAPTCHA error:", error);
      }
      else if (currentRequestError === false) {
        setRequestError(true);
        setRequestErrorMsg("There was an Error processing your request.");
      }
      console.error("There was an error!", error);
      setIsLoading(false);
    }
  };

  const reformatForDatePicker = (date: string): Date => {
    const [year, month, day] = date.split("-");
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  return (
    <>
      {!isLoading && (
        <div className={"ct-flex ct-justify-center ct-items-center cardContainer"}>
          <CTCard expandable={false} className='ct-px-1 ct-mb-4'>
            <CTCard.Header className="ct-px-4 ct-py-2 ct-border-t-[5px] ct-border-t-primary-500 ct-bg-cloudy-gray-500 ct-h-[75px]">
              <div className={"ct-flex ct-justify-between ct-items-center"} >
                <h1 className="ct-text-primary-500 ct-text-[22px] ct-font-bold ct-m-0 ct-leading-[1.5] ct-font-rock ct-text-nowrap">
                  {"Let's get you registered"}
                </h1>
              </div>
            </CTCard.Header>
            <CTCard.Content className='ct-font-sans'>
              <div className="ct-p-4 ct-space-y-1">
                <p className="ct-mb-[30px]">
                  Enter the personal information you shared with your employer.
                </p>
                <div className="formLayout">
                  {/* First Name */}
                  <CTLabel className='ct-col-span-3 ct-text-left ct-text-black ct-text-[1rem]' htmlFor="firstName">First Name*</CTLabel>
                  <CTInput id="firstName" disabled={isLoading} value={firstName} onChange={(e: string) => {
                    setFirstName(e);
                  }} containerClassName='ct-col-span-9' type="text" />

                  {/* Last Name */}
                  <CTLabel className='ct-col-span-3 ct-text-left ct-text-black ct-text-[1rem]' htmlFor="lastName">Last Name*</CTLabel>
                  <CTInput id="lastName" disabled={isLoading} value={lastName} onChange={(e: string) => {
                    setLastName(e);
                  }} containerClassName='ct-col-span-9' type="text" />

                  {/* Last 4 digits of SSN */}
                  <CTLabel className='ct-col-span-3 ct-text-left ct-text-black ct-text-[1rem]' htmlFor="ssn">Last 4 digits of SSN*</CTLabel>
                  <div className='ct-col-span-9 ct-flex-1 ct-flex ct-items-center'>
                    <CTInput id="ssn" visibleNumberOfChars={0} disabled={isLoading} value={last4OfSocial} maxLength={4} onChange={(e: string) => {
                      setLast4OfSocial(e);
                    }} containerClassName="ct-flex-1" className="ct-rounded-none ct-rounded-l" type={shouldDisplaySSN ? "text" : "password"} />
                    <CTButton
                      className='ct-flex-none ct-h-[40px] ct-w-[38px] ct-border-solid ct-border-0 ct-border-r ct-border-b ct-border-t
                        ct-bg-storm-gray-0 ct-text-storm-gray-700  ct-border-iron-gray-100 ct-rounded-none ct-rounded-r hover:ct-bg-storm-gray-0'
                      aria-label={shouldDisplaySSN ? "Hide SSN" : "Show SSN"}
                      iconSize={15}
                      icon={shouldDisplaySSN ? "eye" : "eye-slash"}
                      onClick={() => setShouldDisplaySSN(!shouldDisplaySSN)} >
                    </CTButton>
                  </div>

                  {/* Date of Birth */}
                  <CTLabel className='ct-col-span-3 ct-text-left ct-text-black ct-text-[1rem]' htmlFor="dob">DOB*</CTLabel>
                  <div className="ct-col-span-9 ct-flex-grow ct-items-center">
                    <CTDatePicker
                      id={"dob"}
                      disabled={isLoading}
                      maxDate={new Date()}
                      selected={dateOfBirth == "" ? null : reformatForDatePicker(dateOfBirth)}
                      dateFormat='MM/dd/yyyy'
                      onChange={(date: Date | null) => {
                        let formattedDate = "";
                        if (date) {
                          const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate().toString();
                          const month = (date.getMonth() + 1) < 10 ? `0${date.getMonth() + 1}` : (date.getMonth() + 1).toString();
                          const year = date.getFullYear();
                          formattedDate = `${year}-${month}-${day}`;
                        }

                        setDateOfBirth(formattedDate);
                      }}
                    />
                  </div>

                  {/* SSO ID */}
                  <CTLabel className='ct-col-span-3 ct-text-left ct-text-black ct-text-[1rem]' htmlFor="nbcSso">SSO ID</CTLabel>
                  <CTInput id="nbcSso" disabled={isLoading} value={nbcSso} onChange={(e: string) => {
                    setNbcSso(e);
                  }} className="ct-w-full" containerClassName='ct-col-span-9' type="text" />

                  {/* Email Text */}
                  <p className='ct-col-span-12 ct-text-left ct-text-black ct-text-[1rem]'>
                    Please enter your personal email. Your personal email will be your username.
                  </p>

                  {/* Personal Email */}
                  <CTLabel className='ct-col-span-3 ct-text-left ct-text-black ct-text-[1rem]' htmlFor="personalEmail">Personal Email*</CTLabel>
                  <CTInput id="personalEmail" disabled={isLoading} value={personalEmail}
                    onChange={(e: string) => {
                      setPersonalEmail(e);
                      if (customEmailValidator(e)) {
                        setValidEmailFormat(true);
                      }
                      else {
                        setValidEmailFormat(false);
                      }
                    }}
                    containerClassName='ct-col-span-9' className="ct-w-full" type="email" onChangeDebounceTime={500} />
                  {
                    validEmailFormat === false &&
                    <div className="ct-align-middle ct-bg-roman-red-500 ct-col-span-12 ct-break-words ct-font-sans
                      ct-my-[.1em] ct-text-white ct-font-bold ct-p-1 ct-rounded-[5px] ct-text-center ct-italic">
                      <p className="ct-m-0">Please provide a valid email address.</p>
                    </div>
                  }
                  {warnEmail && validEmailFormat &&
                    <div className="ct-align-middle ct-bg-honey-500 ct-col-span-12 ct-break-words ct-font-sans ct-my-[.1em]
                      ct-text-black ct-font-bold ct-p-1 ct-rounded-[5px] ct-text-center ct-italic">
                      <p className="ct-m-0">Please use your personal email instead of a company email.</p>
                    </div>
                  }

                  {/* Re-enter Personal Email */}
                  <CTLabel className='ct-col-span-3 ct-text-left ct-text-black ct-text-[1rem]' htmlFor="personalEmailReenter">Re-enter Email*</CTLabel>
                  <CTInput id="personalEmailReenter" disabled={isLoading} value={formEmailReenter}
                    onChange={(e: string) => {
                      setFormEmailReenter(e);
                    }}
                    containerClassName='ct-col-span-9' className="ct-w-full" type="email" />

                  {invalidEmailMatch &&
                    <div className="ct-align-middle ct-bg-roman-red-500 ct-col-span-12 ct-break-words ct-font-sans ct-my-[.1em]
                      ct-text-white ct-font-bold ct-p-1 ct-rounded-[5px] ct-text-center ct-italic">
                      <p className="ct-m-0">Entered emails do not match, please try again.</p>
                    </div>
                  }
                </div>

                {/* Submit Section */}
                <div className="md:ct-flex md:ct-flex-row md:ct-items-end md:ct-justify-between sm:ct-flex-col sm:ct-flex">
                  <div className="ct-flex ct-justify-end ct-mt-1">
                    <CTButton
                      disabled={invalidEmailMatch || !dateOfBirth || !firstName || !lastName || !last4OfSocial || !personalEmail ||
                        !formEmailReenter || !validEmailFormat || isLoading}
                      onClick={onSubmit} primary
                      raised>Submit</CTButton>
                  </div>
                </div>
              </div>
            </CTCard.Content>
          </CTCard >
        </div>
      )}
      {requestError &&
        <div className="ct-flex ct-justify-center ct-items-center ct-px-1">
          <div className="ct-text-center ct-max-w-[775px] ct-mb-4">
            <p className="ct-font-bold ct-text-roman-red-500">
              {requestErrorMsg}
            </p>
            {maskedEmail &&
              <>
                <p className="ct-font-bold ct-text-roman-red-500">
                  {`Your account is registered to: ${maskedEmail}`}
                </p>
                <p className="ct-text-storm-gray-700">
                  {"In the future, you can use "}
                  <Link to="/forgot-username" className="ct-text-primary-500 ct-underline">
                    Forgot Username
                  </Link>
                  {" to see your email hint."}
                </p>
              </>
            }
          </div>
        </div>
      }
      {isLoading && (
        <div className="ct-fixed ct-top-0 ct-left-0 ct-right-0 ct-bottom-0 ct-flex ct-items-center ct-justify-center ct-bg-white ct-z-50 ct-overflow-hidden">
          <div className="ct-items-center ct-justify-center ct-m-4">
            <p className="ct-text-center ct-text-storm-gray-700 ct-font-bold ct-font-sans">
              {"We're getting you registered - this may take up to 30 seconds."}
            </p>
            <br />
            <p className="ct-text-center ct-text-storm-gray-700 ct-font-sans">
              {"Please don't close or refresh this window. You'll be automatically redirected once the process is complete."}
            </p>
            <div className="ct-flex ct-items-center ct-justify-center ct-text-primary-500 ct-mt-4">
              <CTProgressCircular />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RegistrationForm;

